import React, { useState, useEffect } from 'react';
import { sports, leagueData, betTypes } from '../constants/data';
import { toast } from 'react-toastify';

const BetInput = ({ onSubmit, onClose, user, copiedBet }) => {
  const [formData, setFormData] = useState({
    sport: copiedBet?.sport || '',
    league: copiedBet?.league || '',
    homeTeam: copiedBet?.homeTeam || '',
    awayTeam: copiedBet?.awayTeam || '',
    betType: copiedBet?.betType || '',
    selection: copiedBet?.selection || '',
    odds: copiedBet?.odds || '',
    stake: copiedBet?.stake || '',
    units: copiedBet?.units || '1',
    confidence: copiedBet?.confidence || 'medium',
    analysis: copiedBet?.analysis || '',
    tipster: copiedBet?.tipster || user?.displayName || user?.email || '',
    isPublic: copiedBet?.isPublic !== undefined ? copiedBet.isPublic : true,
    isTipsterBet: user?.userType === 'tipster' || user?.isTipster || false,
    placedOnTime: copiedBet?.placedOnTime !== undefined ? copiedBet.placedOnTime : true,
    eventDate: copiedBet?.eventDate || '',
    eventTime: copiedBet?.eventTime || '',
    closingLine: copiedBet?.closingLine || '',
    isMultiLeg: copiedBet?.isMultiLeg || false,
    legs: copiedBet?.legs || []
  });

  const [currentLeg, setCurrentLeg] = useState({
    match: '',
    bet: '',
    odds: ''
  });

  const [availableLeagues, setAvailableLeagues] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [availableBetTypes, setAvailableBetTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('BetInput initialized with user:', user);
    console.log('Initial formData:', formData);
  }, []);

  // Update available leagues when sport changes
  useEffect(() => {
    console.log('Sport changed to:', formData.sport);
    if (formData.sport && leagueData[formData.sport]) {
      const leagues = Object.keys(leagueData[formData.sport].leagues);
      console.log('Available leagues for', formData.sport, ':', leagues);
      setAvailableLeagues(leagues);
      
      // Auto-select if only one league available
      if (leagues.length === 1) {
        setFormData(prev => ({ ...prev, league: leagues[0] }));
      }
    } else {
      setAvailableLeagues([]);
    }
    
    // Reset dependent fields when sport changes
    if (!copiedBet) {
      setFormData(prev => ({ 
        ...prev, 
        league: '', 
        homeTeam: '', 
        awayTeam: '',
        betType: '',
        selection: ''
      }));
    }
    setAvailableTeams([]);
  }, [formData.sport]);

  // Update available teams when league changes
  useEffect(() => {
    console.log('League changed to:', formData.league, 'for sport:', formData.sport);
    if (formData.sport && formData.league && leagueData[formData.sport]?.leagues[formData.league]) {
      const teams = leagueData[formData.sport].leagues[formData.league];
      console.log('Available teams for', formData.league, ':', teams);
      setAvailableTeams(teams);
    } else {
      setAvailableTeams([]);
    }
    
    // Reset team selection when league changes
    if (!copiedBet) {
      setFormData(prev => ({ ...prev, homeTeam: '', awayTeam: '' }));
    }
  }, [formData.sport, formData.league]);

  // Update available bet types when sport changes
  useEffect(() => {
    console.log('Setting bet types for sport:', formData.sport);
    if (formData.sport && betTypes[formData.sport]) {
      const types = betTypes[formData.sport];
      console.log('Available bet types for', formData.sport, ':', types);
      setAvailableBetTypes(types);
    } else {
      setAvailableBetTypes([]);
    }
  }, [formData.sport]);

  const addLeg = () => {
    if (!currentLeg.match || !currentLeg.bet || !currentLeg.odds) {
      return toast.error('Please fill all leg details');
    }
    
    if (parseFloat(currentLeg.odds) <= 1.0) {
      return toast.error('Odds must be greater than 1.0');
    }

    setFormData(prev => ({
      ...prev,
      legs: [...prev.legs, currentLeg]
    }));
    setCurrentLeg({ match: '', bet: '', odds: '' });
    toast.success('Leg added successfully');
  };

  const removeLeg = (index) => {
    setFormData(prev => ({
      ...prev,
      legs: prev.legs.filter((_, i) => i !== index)
    }));
    toast.info('Leg removed');
  };

  const calculateMultiOdds = () => {
    if (formData.legs.length === 0) return '1.00';
    return formData.legs.reduce((acc, leg) => acc * parseFloat(leg.odds || 1), 1).toFixed(2);
  };

  const generateMatchName = () => {
    if (formData.isMultiLeg) {
      return `Multi (${formData.legs.length} legs)`;
    }
    
    if (formData.homeTeam && formData.awayTeam) {
      return `${formData.homeTeam} vs ${formData.awayTeam}`;
    }
    
    if (formData.selection) {
      return formData.selection;
    }
    
    return 'Manual Entry';
  };

  const generateBetDescription = () => {
    if (formData.isMultiLeg) {
      return formData.legs.map(leg => `${leg.match}: ${leg.bet}`).join(' | ');
    }
    
    let description = '';
    
    if (formData.betType && formData.selection && formData.betType !== formData.selection) {
      description = `${formData.betType} - ${formData.selection}`;
    } else if (formData.betType) {
      description = formData.betType;
    } else if (formData.selection) {
      description = formData.selection;
    } else {
      description = 'Manual Entry';
    }
    
    return description;
  };

  const validateForm = () => {
    // Required fields
    if (!formData.sport) {
      toast.error('Please select a sport');
      return false;
    }

    if (!formData.units || parseFloat(formData.units) <= 0) {
      toast.error('Units must be greater than 0');
      return false;
    }

    if (formData.isMultiLeg) {
      if (formData.legs.length < 2) {
        toast.error('Multi-leg bets require at least 2 legs');
        return false;
      }
    } else {
      if (!formData.odds || parseFloat(formData.odds) <= 1.0) {
        toast.error('Please enter valid odds (greater than 1.0)');
        return false;
      }
    }

    // UFC and Tennis require manual match entry
    if ((formData.sport === 'UFC' || formData.sport === 'Tennis') && !formData.selection) {
      toast.error(`Please enter ${formData.sport === 'UFC' ? 'fight' : 'match'} details`);
      return false;
    }

    // Other sports should have either teams selected or manual entry
    if (formData.sport !== 'UFC' && formData.sport !== 'Tennis' && !formData.homeTeam && !formData.awayTeam && !formData.selection) {
      toast.error('Please select teams or enter match details manually');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    console.log('Starting form submission...');
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const finalOdds = formData.isMultiLeg ? calculateMultiOdds() : formData.odds;
      const match = generateMatchName();
      const bet = generateBetDescription();

      const betData = {
        // Core bet info
        sport: formData.sport,
        league: formData.league || null,
        match: match,
        bet: bet,
        betType: formData.betType || null,
        selection: formData.selection || null,
        odds: parseFloat(finalOdds),
        units: parseFloat(formData.units),
        stake: formData.stake ? parseFloat(formData.stake) : null,
        confidence: formData.confidence,
        
        // Teams (if applicable)
        homeTeam: formData.homeTeam || null,
        awayTeam: formData.awayTeam || null,
        
        // Multi-leg specific
        isMultiLeg: formData.isMultiLeg,
        legs: formData.legs,
        
        // Metadata
        tipster: formData.tipster,
        tipsterUid: user?.uid || null,
        userEmail: user?.email || null,
        isPublic: formData.isPublic,
        isTipsterBet: formData.isTipsterBet,
        placedOnTime: formData.placedOnTime,
        
        // Event timing
        eventDate: formData.eventDate || null,
        eventTime: formData.eventTime || null,
        eventDateTime: (formData.eventDate && formData.eventTime) 
          ? `${formData.eventDate}T${formData.eventTime}` 
          : formData.eventDate || null,
        
        // Additional
        analysis: formData.analysis || null,
        closingLine: formData.closingLine || null,
        
        // Status
        status: 'pending'
      };

      console.log('Submitting bet data:', betData);

      // Call the parent's onSubmit function
      await onSubmit(betData);
      
      toast.success(`${formData.isTipsterBet ? 'Tip' : 'Bet'} added successfully!`);
      onClose();
      
    } catch (error) {
      console.error('Error submitting bet:', error);
      toast.error('Failed to add bet. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isUFC = formData.sport === 'UFC';
  const isTennis = formData.sport === 'Tennis';
  const shouldShowTeams = !isUFC && !isTennis && availableTeams.length > 0;
  const shouldShowManualEntry = isUFC || isTennis || (!formData.homeTeam && !formData.awayTeam);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          {copiedBet ? 'Copy Bet from Tipster' : formData.isTipsterBet ? 'Add New Tip' : 'Track Bet'}
        </h2>
        
        {copiedBet && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
            <p className="text-sm text-blue-700">
              Copying bet from <strong>{copiedBet.tipster}</strong>. You can modify the units and stake to match your bankroll.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {/* Multi-leg toggle */}
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              id="isMultiLeg"
              className="mr-2"
              checked={formData.isMultiLeg}
              onChange={(e) => setFormData(prev => ({ ...prev, isMultiLeg: e.target.checked }))}
            />
            <label htmlFor="isMultiLeg" className="text-sm font-medium text-gray-700">
              Multi-leg bet
            </label>
          </div>

          {!formData.isMultiLeg ? (
            <>
              {/* Sport Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sport *</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                  value={formData.sport}
                  onChange={(e) => setFormData(prev => ({ ...prev, sport: e.target.value }))}
                >
                  <option value="">Select Sport</option>
                  {sports.map(sport => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>
              </div>

              {/* League Selection */}
              {formData.sport && availableLeagues.length > 0 && !isUFC && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isTennis ? 'Tournament' : 'League'}
                  </label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                    value={formData.league}
                    onChange={(e) => setFormData(prev => ({ ...prev, league: e.target.value }))}
                  >
                    <option value="">Select {isTennis ? 'Tournament' : 'League'}</option>
                    {availableLeagues.map(league => (
                      <option key={league} value={league}>{league}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Team Selection */}
              {shouldShowTeams && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Home Team</label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                      value={formData.homeTeam}
                      onChange={(e) => setFormData(prev => ({ ...prev, homeTeam: e.target.value }))}
                    >
                      <option value="">Select Home Team</option>
                      {availableTeams.map(team => (
                        <option key={team} value={team}>{team}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Away Team</label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                      value={formData.awayTeam}
                      onChange={(e) => setFormData(prev => ({ ...prev, awayTeam: e.target.value }))}
                    >
                      <option value="">Select Away Team</option>
                      {availableTeams.filter(team => team !== formData.homeTeam).map(team => (
                        <option key={team} value={team}>{team}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Manual Match Entry */}
              {shouldShowManualEntry && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isUFC ? 'Fight/Event *' : isTennis ? 'Match/Tournament *' : 'Match/Event'}
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                    value={formData.selection}
                    onChange={(e) => setFormData(prev => ({ ...prev, selection: e.target.value }))}
                    placeholder={
                      isUFC ? "e.g., Jones vs Smith" : 
                      isTennis ? "e.g., Djokovic vs Nadal" :
                      "Enter match details"
                    }
                  />
                </div>
              )}

              {/* Bet Type Selection */}
              {formData.sport && availableBetTypes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bet Type</label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                    value={formData.betType}
                    onChange={(e) => setFormData(prev => ({ ...prev, betType: e.target.value }))}
                  >
                    <option value="">Select Bet Type</option>
                    {availableBetTypes.map(betType => (
                      <option key={betType} value={betType}>{betType}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Selection/Details */}
              {!isUFC && !isTennis && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Selection/Details
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                    value={formData.selection}
                    onChange={(e) => setFormData(prev => ({ ...prev, selection: e.target.value }))}
                    placeholder="e.g., Over 2.5, Team A -6.5, Player Name Over 20.5"
                  />
                </div>
              )}

              {/* Odds */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Odds *</label>
                <input
                  type="number"
                  step="0.01"
                  min="1.01"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                  value={formData.odds}
                  onChange={(e) => setFormData(prev => ({ ...prev, odds: e.target.value }))}
                  placeholder="1.91"
                />
              </div>
            </>
          ) : (
            /* Multi-leg bet builder */
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="font-medium mb-3">Add Leg</h4>
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Match (e.g., Team A vs Team B)"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={currentLeg.match}
                    onChange={(e) => setCurrentLeg(prev => ({ ...prev, match: e.target.value }))}
                  />
                  <input
                    type="text"
                    placeholder="Bet (e.g., Over 2.5 goals)"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={currentLeg.bet}
                    onChange={(e) => setCurrentLeg(prev => ({ ...prev, bet: e.target.value }))}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="1.01"
                    placeholder="Odds"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={currentLeg.odds}
                    onChange={(e) => setCurrentLeg(prev => ({ ...prev, odds: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={addLeg}
                    className="w-full bg-green-500 text-white py-2 rounded-md hover:bg-green-600"
                  >
                    Add Leg
                  </button>
                </div>
              </div>

              {/* Display current legs */}
              {formData.legs.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">Legs ({formData.legs.length}):</h4>
                  {formData.legs.map((leg, index) => (
                    <div key={index} className="flex justify-between items-center bg-white p-2 rounded border">
                      <div className="text-sm">
                        <span className="font-medium">{leg.match}</span>: {leg.bet} @ {leg.odds}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLeg(index)}
                        className="text-red-500 hover:text-red-700 px-2 py-1 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <div className="text-right font-medium">
                    Combined Odds: {calculateMultiOdds()}
                  </div>
                </div>
              )}

              {/* Sport for multi-leg */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Sport *</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                  value={formData.sport}
                  onChange={(e) => setFormData(prev => ({ ...prev, sport: e.target.value }))}
                >
                  <option value="">Select Primary Sport</option>
                  {sports.map(sport => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Units and Stake */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Units *</label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                value={formData.units}
                onChange={(e) => setFormData(prev => ({ ...prev, units: e.target.value }))}
                placeholder="1.0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stake (Optional)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                value={formData.stake}
                onChange={(e) => setFormData(prev => ({ ...prev, stake: e.target.value }))}
                placeholder="Your stake amount"
              />
            </div>
          </div>

          {/* Event Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Date</label>
              <input
                type="date"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                value={formData.eventDate}
                onChange={(e) => setFormData(prev => ({ ...prev, eventDate: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Time</label>
              <input
                type="time"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                value={formData.eventTime}
                onChange={(e) => setFormData(prev => ({ ...prev, eventTime: e.target.value }))}
              />
            </div>
          </div>

          {/* Confidence Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confidence Level</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
              value={formData.confidence}
              onChange={(e) => setFormData(prev => ({ ...prev, confidence: e.target.value }))}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          {/* Tipster Name for Punters */}
          {!formData.isTipsterBet && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipster Name</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
                value={formData.tipster}
                onChange={(e) => setFormData(prev => ({ ...prev, tipster: e.target.value }))}
                placeholder="Who gave you this tip?"
              />
            </div>
          )}

          {/* Analysis */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Analysis (Optional)</label>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300"
              rows="3"
              value={formData.analysis}
              onChange={(e) => setFormData(prev => ({ ...prev, analysis: e.target.value }))}
              placeholder="Why this bet will win..."
            />
          </div>

          {/* Placed on Time for Punters */}
          {!formData.isTipsterBet && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="placedOnTime"
                className="mr-2"
                checked={formData.placedOnTime}
                onChange={(e) => setFormData(prev => ({ ...prev, placedOnTime: e.target.checked }))}
              />
              <label htmlFor="placedOnTime" className="text-sm text-gray-700">
                I placed this bet on time
              </label>
            </div>
          )}

          {/* Public/Private Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isPublic"
              className="mr-2"
              checked={formData.isPublic}
              onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
            />
            <label htmlFor="isPublic" className="text-sm text-gray-700">
              Make this {formData.isTipsterBet ? 'tip' : 'bet'} public
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 bg-gradient-to-r from-green-400 to-blue-400 text-white py-2 rounded-md hover:opacity-90 transition disabled:opacity-50"
            >
              {isSubmitting ? 'Adding...' : `Add ${formData.isTipsterBet ? 'Tip' : 'Bet'}`}
            </button>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-400 transition disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetInput;